// src/utils/money.model.ts
var MONEY_DECIMALS = 1;
var ROUND_FACTOR = 10 ** MONEY_DECIMALS;
function roundMoney(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const scaled = value * ROUND_FACTOR;
  const roundedScaled = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return roundedScaled / ROUND_FACTOR;
}

// src/pos/ordering.model.ts
var QUICK_CHARGE_CODE_PREFIX = "QC_";
var KIOSK_DINE_IN_CODE_PREFIX = "KI_";
function isQuickChargeOrderCode(orderCode) {
  return orderCode?.startsWith(QUICK_CHARGE_CODE_PREFIX) ?? false;
}
function isKioskDineInOrderCode(orderCode) {
  return orderCode?.startsWith(KIOSK_DINE_IN_CODE_PREFIX) ?? false;
}

// src/pos/min-order.model.ts
var MIN_ORDER_COMPLETION_NOTE_PREFIX = "\u05D4\u05E9\u05DC\u05DE\u05D4 \u05DC\u05DE\u05D9\u05E0\u05D9\u05DE\u05D5\u05DD";
var MIN_ORDER_COMPLETION_LABEL = "\u05D4\u05E9\u05DC\u05DE\u05EA \u05DE\u05D9\u05E0\u05D9\u05DE\u05D5\u05DD \u05D4\u05D6\u05DE\u05E0\u05D4";
function isMinOrderCompletionNote(note) {
  return typeof note === "string" && note.startsWith(MIN_ORDER_COMPLETION_NOTE_PREFIX);
}
function resolveEffectiveMinimumOrder(config, dinersCount) {
  if (config.minimumOrderPerDiner > 0) {
    const diners = Number.isFinite(dinersCount) && dinersCount > 0 ? Math.floor(dinersCount) : 0;
    return roundMoney(config.minimumOrderPerDiner * diners);
  }
  return config.minimumOrder > 0 ? roundMoney(config.minimumOrder) : 0;
}

// src/pos/cash-alterations.model.ts
import { z } from "zod";
var CASH_ALTERATION_ACTIONS = ["entry-cash", "in", "out"];
var cashAlterationActionSchema = z.enum(CASH_ALTERATION_ACTIONS);

// src/pos/tips.model.ts
function resolveTip(mode, value, base) {
  if (mode == null || value == null) {
    return 0;
  }
  if (mode === "value") {
    return roundMoney(value);
  }
  if (value <= 0) {
    return 0;
  }
  return roundMoney(base * value / 100);
}
function allocateGlobalTipShare(remainingTip, lineAmount, remainingItems, isClosingLine) {
  if (remainingTip <= 0) {
    return 0;
  }
  if (isClosingLine) {
    return roundMoney(Math.max(0, remainingTip));
  }
  if (remainingItems <= 0) {
    return 0;
  }
  return roundMoney(remainingTip * lineAmount / remainingItems);
}
function allocateProportionalTip(total, lineAmounts) {
  let remainingTip = total;
  let remainingItems = roundMoney(lineAmounts.reduce((sum, amount) => sum + amount, 0));
  return lineAmounts.map((amount, index) => {
    const isClosingLine = index === lineAmounts.length - 1;
    const share = allocateGlobalTipShare(remainingTip, amount, remainingItems, isClosingLine);
    remainingTip = roundMoney(remainingTip - share);
    remainingItems = roundMoney(remainingItems - amount);
    return share;
  });
}
function isEqualSplit(lineAmounts) {
  if (lineAmounts.length < 2) {
    return true;
  }
  return roundMoney(Math.max(...lineAmounts) - Math.min(...lineAmounts)) <= 0.1;
}
function allocateEvenTotalTip(total, lineAmounts, itemsTotal) {
  const n = lineAmounts.length;
  const grandTenths = Math.round(roundMoney(itemsTotal + total) * 10);
  const baseTenths = Math.floor(grandTenths / n);
  const extra = grandTenths - baseTenths * n;
  const shares = lineAmounts.map((amount, index) => {
    const targetTenths = baseTenths + (index >= n - extra ? 1 : 0);
    return roundMoney(targetTenths / 10 - amount);
  });
  return shares.some((share) => share < 0) ? null : shares;
}
function allocateGlobalTip(globalTip, lineAmounts) {
  const total = roundMoney(globalTip);
  const itemsTotal = roundMoney(lineAmounts.reduce((sum, amount) => sum + amount, 0));
  const allZeroLines = lineAmounts.length > 0 && lineAmounts.every((amount) => roundMoney(amount) === 0);
  if (total <= 0 || itemsTotal <= 0 && !allZeroLines) {
    return lineAmounts.map(() => 0);
  }
  if (isEqualSplit(lineAmounts)) {
    const evenTotal = allocateEvenTotalTip(total, lineAmounts, itemsTotal);
    if (evenTotal) {
      return evenTotal;
    }
  }
  return allocateProportionalTip(total, lineAmounts);
}

// src/pos/area-print-rules.model.ts
import { z as z2 } from "zod";
var areaPrintRulePrinterMappingSchema = z2.object({
  printerId: z2.number().int().positive(),
  areaIds: z2.array(z2.number().int().positive())
}).strict();
var areaPrintRuleSchema = z2.object({
  id: z2.string().trim().min(1).max(120),
  name: z2.string().trim().min(1).max(120),
  productIds: z2.array(z2.number().int().positive()),
  printerMappings: z2.array(areaPrintRulePrinterMappingSchema).min(2)
}).strict();
var updateAreaPrintRulesSchema = z2.object({
  areaPrintRules: z2.array(areaPrintRuleSchema)
}).strict();

// src/pos/stream-events.model.ts
var POS_STREAM_EVENT_TYPES = [
  "order.opened",
  "order.upserted",
  "order.closed",
  "orderline.added",
  "orderline.updated",
  "orderline.item_payment_updated",
  "orderline.removed",
  "orderline.sent",
  "paymentline.created",
  "paymentline.done",
  "credit.tap.pending",
  "credit.tap.released",
  "credit.transaction.pending",
  "credit.transaction.resolved",
  "session.opened",
  "session.closed",
  "session.cash_alterations_updated",
  "cashDrawer.notConfigured",
  "session.daily_brief_updated",
  "session.auto_close_time_updated",
  "tenant.floor_background_updated",
  "tenant.general_item_requires_manager_approval_updated",
  "tenant.orders_page_waiter_own_visibility_updated",
  "tenant.manual_credit_entry_blocked_updated",
  "tenant.stationary_name_updated",
  "tenant.open_drawer_on_partial_cash_updated",
  "tenant.open_drawer_employee_ids_updated",
  "tenant.open_drawer_requires_password_updated",
  "tenant.refund_code_via_push_updated",
  "tenant.employee_attendance_report_enabled_updated",
  "tenant.attendance_report_rules_updated",
  "area.upserted",
  "table.upserted",
  "table.removed",
  "employee.upserted",
  "employee.deactivated",
  "employee.kicked",
  "product.upserted",
  "product.deactivated",
  "payment-terminal.upserted",
  "payment-terminal.active-changed",
  "category.upserted",
  "printer.upserted",
  "happy_hour.changed",
  "variantComboGroup.upserted",
  "variantComboGroup.removed",
  "merge.links.changed",
  "approval_request.created",
  "approval_request.decided",
  "tenant.maintenance-mode-changed",
  "pos.refresh-requested",
  "employee.presence.probe.requested",
  "green_fire.rescue.requested",
  "employee.presence.probe.acknowledged"
];

// src/pos/past-receipts.model.ts
import { z as z3 } from "zod";
var CLOSED_ORDERS_PAGE_SIZE_MAX = 50;
var CLOSED_ORDERS_PAGE_SIZE_DEFAULT = 30;
var SESSION_FILTER_OPTIONS_LIMIT = 200;
var closedOrdersQuerySchema = z3.object({
  page: z3.coerce.number().int().min(1).default(1),
  pageSize: z3.coerce.number().int().min(1).max(CLOSED_ORDERS_PAGE_SIZE_MAX).default(CLOSED_ORDERS_PAGE_SIZE_DEFAULT),
  dateFrom: z3.string().trim().min(1).optional(),
  dateTo: z3.string().trim().min(1).optional(),
  search: z3.string().trim().min(1).max(120).optional(),
  customer: z3.string().trim().min(1).max(120).optional(),
  sessionId: z3.coerce.number().int().min(1).optional()
});

// src/pos/accountant-report.model.ts
import { z as z4 } from "zod";
var accountantReportRangeSchema = z4.object({
  from: z4.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z4.string().regex(/^\d{4}-\d{2}-\d{2}$/)
}).strict().refine((value) => value.from <= value.to, { message: "from must be on or before to", path: ["from"] });
var accountantReportSessionsSchema = z4.object({
  sessionIds: z4.array(z4.number().int().positive()).min(1)
}).strict();

// src/pos/attendance-rules.model.ts
var DEFAULT_ATTENDANCE_REPORT_RULES = {
  overtime125StartHours: 8,
  overtime150StartHours: 10,
  useNightShiftOvertimeRules: false,
  overtime150Range: null,
  overtime150Weekdays: [],
  overtime200SpecialDates: []
};

// src/pos/assistant.model.ts
var ASSISTANT_QUESTION_MIN_LENGTH = 2;
var ASSISTANT_QUESTION_MAX_LENGTH = 500;
var ASSISTANT_FEEDBACK_MIN_LENGTH = 2;
var ASSISTANT_FEEDBACK_MAX_LENGTH = 2e3;
var ASSISTANT_HISTORY_MAX_TURNS = 6;
var ASSISTANT_HISTORY_TURN_MAX_LENGTH = 1e3;

// src/pos/z-report-layout.model.ts
import { z as z5 } from "zod";
var zReportLayoutEntryTypeSchema = z5.enum(["section", "row"]);
var zReportLayoutEntrySchema = z5.object({
  key: z5.string().trim().min(1).max(120),
  sourceKey: z5.string().trim().min(1).max(120).optional(),
  type: zReportLayoutEntryTypeSchema,
  visible: z5.boolean(),
  bold: z5.boolean().optional(),
  negative: z5.boolean().optional(),
  label: z5.string().trim().min(1).max(120)
}).strict();
var zReportLayoutConfigSchema = z5.object({
  version: z5.literal(1),
  entries: z5.array(zReportLayoutEntrySchema).max(400)
}).strict();

export {
  MONEY_DECIMALS,
  roundMoney,
  QUICK_CHARGE_CODE_PREFIX,
  KIOSK_DINE_IN_CODE_PREFIX,
  isQuickChargeOrderCode,
  isKioskDineInOrderCode,
  MIN_ORDER_COMPLETION_NOTE_PREFIX,
  MIN_ORDER_COMPLETION_LABEL,
  isMinOrderCompletionNote,
  resolveEffectiveMinimumOrder,
  CASH_ALTERATION_ACTIONS,
  cashAlterationActionSchema,
  resolveTip,
  allocateGlobalTipShare,
  allocateGlobalTip,
  areaPrintRulePrinterMappingSchema,
  areaPrintRuleSchema,
  updateAreaPrintRulesSchema,
  POS_STREAM_EVENT_TYPES,
  CLOSED_ORDERS_PAGE_SIZE_MAX,
  CLOSED_ORDERS_PAGE_SIZE_DEFAULT,
  SESSION_FILTER_OPTIONS_LIMIT,
  closedOrdersQuerySchema,
  accountantReportRangeSchema,
  accountantReportSessionsSchema,
  DEFAULT_ATTENDANCE_REPORT_RULES,
  ASSISTANT_QUESTION_MIN_LENGTH,
  ASSISTANT_QUESTION_MAX_LENGTH,
  ASSISTANT_FEEDBACK_MIN_LENGTH,
  ASSISTANT_FEEDBACK_MAX_LENGTH,
  ASSISTANT_HISTORY_MAX_TURNS,
  ASSISTANT_HISTORY_TURN_MAX_LENGTH,
  zReportLayoutEntryTypeSchema,
  zReportLayoutEntrySchema,
  zReportLayoutConfigSchema
};
